import { RequestStatus, WaitlistRequest } from "../types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../utils/utils";
import { sessionExpired } from "./SessionSlice";
import { Pagination } from "../components/table/Table";
import { RootState } from "../store";

export interface ToastMessageConfig {
  message: string;
  type: "error" | "success" | "info";
  followUpConfig?: {
    buttonText: string;
    onButtonClick: () => void;
  };
}

export interface WaitlistRequestsState {
  allWaitlistRequests: WaitlistRequest[];
  filteredWaitlistRequests: WaitlistRequest[];
  isLoading: boolean;
  pagination: Pagination;
  requestOnView: WaitlistRequest | null;
  toastMessageConfig: ToastMessageConfig | null;
  selectedStatus: RequestStatus[];
  isApprovingRequest: boolean;
  isDecliningRequest: boolean;
}

const initialState: WaitlistRequestsState = {
  allWaitlistRequests: [],
  filteredWaitlistRequests: [],
  isLoading: true,
  toastMessageConfig: null,
  pagination: {
    pageNumber: 1,
    pageSize: 10,
    total: 0,
  },
  requestOnView: null,
  selectedStatus: [],
  isApprovingRequest: false,
  isDecliningRequest: false,
};

export const updateWaitlistRequest = createAsyncThunk<
  { waitlistRequestStatus: RequestStatus; requestId: string },
  { waitlistRequestStatus: RequestStatus; requestId: string },
  { rejectValue: Error }
>("waitlist/updateWaitlistRequest", async ({ waitlistRequestStatus, requestId }, { rejectWithValue, getState }) => {
  try {
    const token = (getState() as RootState).auth.user?.token;
    if (!token) {
      return rejectWithValue(new Error("No token found"));
    }
    const response = await api.patch(`/requests/${requestId}`, token, { status: waitlistRequestStatus });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      sessionExpired(true);
    }
    return rejectWithValue(new Error(error.response?.data?.message));
  }
});

export const fetchWaitlistRequestsForOrganization = createAsyncThunk<
  { waitlistRequests: WaitlistRequest[] },
  { token?: string },
  { rejectValue: Error }
>("waitlist/fetchWaitlistRunsForOrganization", async ({ token }, { rejectWithValue, getState }) => {
  try {
    const response = await api.get("/requests", token || ((getState() as RootState).auth.user?.token as string));

    if (response.data) {
      return { waitlistRequests: response.data };
    }
    throw new Error("Invalid response data");
  } catch (error: any) {
    if (error.response?.status === 401) {
      sessionExpired(true);
    }
    return rejectWithValue(new Error(error.response?.data?.message));
  }
});

export const deleteWaitlistRequest = createAsyncThunk<
  { requestId: string },
  { requestId: string },
  { rejectValue: Error }
>("waitlist/deleteWaitlistRequest", async ({ requestId }, { rejectWithValue, getState }) => {
  try {
    const token = (getState() as RootState).auth.user?.token;
    if (!token) {
      return rejectWithValue(new Error("No token found"));
    }
    const response = await api.delete(`/requests/${requestId}`, token);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      sessionExpired(true);
    }
    return rejectWithValue(new Error(error.response?.data?.message));
  }
});

const waitlistRequestsSlice = createSlice({
  name: "waitlistRequests",
  initialState,
  reducers: {
    setWaitlistRequests: (state, action: PayloadAction<WaitlistRequest[]>) => {
      state.allWaitlistRequests = action.payload;
      state.filteredWaitlistRequests = action.payload;
      state.pagination.total = action.payload.length;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setRequestOnView: (state, action: PayloadAction<WaitlistRequest | null>) => {
      state.requestOnView = action.payload;
    },
    setSelectedStatus: (state, action: PayloadAction<RequestStatus[]>) => {
      state.selectedStatus = action.payload;
      state.filteredWaitlistRequests = action.payload.length
        ? state.allWaitlistRequests.filter((request) => action.payload.includes(request.status))
        : state.allWaitlistRequests;
      state.pagination.pageNumber = 1;
      state.pagination.total = state.filteredWaitlistRequests.length;
    },
    clearToast: (state) => {
      state.toastMessageConfig = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWaitlistRequestsForOrganization.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchWaitlistRequestsForOrganization.fulfilled, (state, action) => {
      state.isLoading = false;
      state.allWaitlistRequests = action.payload.waitlistRequests;
      state.filteredWaitlistRequests = action.payload.waitlistRequests;
      state.pagination.total = action.payload.waitlistRequests.length;
      state.pagination.pageNumber = 1;
      if (state.requestOnView) {
        state.requestOnView =
          action.payload.waitlistRequests.find((request) => request.requestId === state.requestOnView?.requestId) ||
          null;
      }
    });
    builder.addCase(fetchWaitlistRequestsForOrganization.rejected, (state, action) => {
      state.isLoading = false;
      state.toastMessageConfig = {
        message: `Failed to fetch waitlist requests: ${action.payload?.message || "Unknown error"}`,
        type: "error",
      };
    });
    builder.addCase(updateWaitlistRequest.pending, (state, action) => {
      if (action.meta.arg.waitlistRequestStatus === RequestStatus.APPROVED) {
        state.isApprovingRequest = true;
      } else {
        state.isDecliningRequest = true;
      }
    });
    builder.addCase(updateWaitlistRequest.fulfilled, (state, action) => {
      state.isApprovingRequest = false;
      state.isDecliningRequest = false;
      state.toastMessageConfig = {
        message: `Waitlist request updated successfully`,
        type: "success",
      };
      fetchWaitlistRequestsForOrganization({});
    });
    builder.addCase(updateWaitlistRequest.rejected, (state, action) => {
      state.isApprovingRequest = false;
      state.isDecliningRequest = false;
      state.toastMessageConfig = {
        message: `Failed to update waitlist request: ${action.payload?.message || "Unknown error"}`,
        type: "error",
      };
      fetchWaitlistRequestsForOrganization({});
    });
    builder.addCase(deleteWaitlistRequest.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteWaitlistRequest.fulfilled, (state, action) => {
      state.isLoading = false;
      state.allWaitlistRequests = state.allWaitlistRequests.filter(
        (request) => request.requestId !== action.meta.arg.requestId
      );
      state.filteredWaitlistRequests = state.filteredWaitlistRequests.filter(
        (request) => request.requestId !== action.meta.arg.requestId
      );
      state.pagination.total = state.filteredWaitlistRequests.length;
      if (state.requestOnView?.requestId === action.meta.arg.requestId) {
        state.requestOnView = null;
      }
      state.toastMessageConfig = {
        message: "Waitlist request resolved successfully",
        type: "success",
      };
    });
    builder.addCase(deleteWaitlistRequest.rejected, (state, action) => {
      state.isLoading = false;
      state.toastMessageConfig = {
        message: `Failed to resolve waitlist request: ${action.payload?.message || "Unknown error"}`,
        type: "error",
      };
    });
  },
});

export const { setWaitlistRequests, setIsLoading, setPagination, setRequestOnView, setSelectedStatus, clearToast } =
  waitlistRequestsSlice.actions;

export default waitlistRequestsSlice.reducer;
